import { isNil, get } from 'lodash';

import { RPA_TYPES } from './RPAs';

/**
 * Gets RPA id.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string} The id of the RPA, or empty string if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getId = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, '_id', '');
};

/**
 * Gets RPA name.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string} The name of the RPA, or empty string if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getName = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'name', '');
};

/**
 * Gets RPA model.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string} The model of the RPA, or empty string if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getModel = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'model', '');
};

/**
 * Gets RPA registration number.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string} The registration number of the RPA, or empty string if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getRegistrationNumber = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'registrationNumber', '');
};

/**
 * Gets RPA construction date.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string} The construction date of the RPA, or empty string if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getConstructionDate = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'constructionDate', '');
};

/**
 * Gets RPA serial number.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string} The serial number of the RPA, or empty string if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getSerialNumber = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'serialNumber', '');
};

/**
 * Gets RPA weight.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {number} The weight of the RPA, or 0 if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getWeight = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'weight', 0);
};

/**
 * Gets RPA type.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string} The type of the RPA, or empty string if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getType = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'rpaType', '');
};

/**
 * Checks if rpa is multicopter.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {boolean} Returns true if rpa is multicopter, false otherwise.
 * @throws {Error} If RPA is null or undefined.
 */
export const isMulticopter = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return getType(rpaObject) === RPA_TYPES.MULTICOPTER;
};

/**
 * Checks if rpa is plane.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {boolean} Returns true if rpa is plane, false otherwise.
 * @throws {Error} If RPA is null or undefined.
 */
export const isPlane = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return getType(rpaObject) === RPA_TYPES.PLANE;
};

/**
 * Gets RPA max descend slope if plane.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {number} The max descend slope for the RPA, or 0 if not found.
 * @throws {Error} If RPA is null, undefined, or multicopter type.
 */
export const getMaxDescendSlope = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  if (isMulticopter(rpaObject)) {
    throw new Error('Multicopters do not have landing slopes');
  }
  return get(rpaObject, 'flightParameters.maxDescendSlope', 0);
};

/**
 * Gets RPA max ascend slope if plane.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {number} The max ascend slope for the RPA, or 0 if not found.
 * @throws {Error} If RPA is null, undefined, or multicopter type.
 */
export const getMaxAscendSlope = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  if (isMulticopter(rpaObject)) {
    throw new Error('Multicopters do not have ascend slopes');
  }
  return get(rpaObject, 'flightParameters.maxAscendSlope', 0);
};

/**
 * Gets RPA optimal landing slope if plane.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {number} The optimal landing slope for the RPA, or 0 if not found.
 * @throws {Error} If RPA is null, undefined, or multicopter type.
 */
export const getOptimalLandingSlope = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  if (isMulticopter(rpaObject)) {
    throw new Error('Multicopters do not have landing slopes');
  }
  return get(rpaObject, 'flightParameters.optimalLandingSlope', 0);
};

/**
 * Gets RPA optimal takeo off slope if plane.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {number} The optimal takeo off slope for the RPA, or 0 if not found.
 * @throws {Error} If RPA is null, undefined, or multicopter type.
 */
export const getOptimalTakeOffSlope = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  if (isMulticopter(rpaObject)) {
    throw new Error('Multicopters do not have take off slopes');
  }
  return get(rpaObject, 'flightParameters.optimalTakeOffSlope', 0);
};

/**
 * Gets RPA max landing speed if multicopter.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {number} The max landing speed for the RPA, or 0 if not found.
 * @throws {Error} If RPA is null, undefined, or plane type.
 */
export const getMaxLandSpeed = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  if (isPlane(rpaObject)) {
    throw new Error('Planes do not have landing speeds');
  }
  return get(rpaObject, 'flightParameters.maxLandSpeed', 0);
};

/**
 * Gets RPA autonomy if multicopter.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {number} The autonomy for the RPA, or 0 if not found.
 * @throws {Error} If RPA is null, undefined, or plane type.
 */
export const getAutonomy = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'flightParameters.autonomy', 0);
};

/**
 * Gets the calibration logs for the RPA.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {object} The object with the calibration logs for the RPA, or empty object if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getCalibrationLogs = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'calibrationLogs', {});
};

/**
 * Gets the log of the accelerometers calibration for the RPA.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string[]} The log of the accelerometers calibration for the RPA, or empty array if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getAccelCalibrationLog = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'calibrationLogs.accelCalibrationLog', []);
};

/**
 * Gets the last entry of the log of the accelerometers calibration for the RPA.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string} The last entry of the log of the accelerometers calibration for the RPA, or '-' if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getAccelCalibrationLogLastEntry = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  const accelCalibrationLog = getAccelCalibrationLog(rpaObject);
  const logLength = accelCalibrationLog.length;
  return get(accelCalibrationLog, `${logLength - 1}`, '-');
};

/**
 * Gets the log of the compass calibration for the RPA.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {string[]} The log of the compass calibration for the RPA, or empty array if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getCompassCalibrationLog = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  return get(rpaObject, 'calibrationLogs.compassCalibrationLog', []);
};

/**
 * Gets the last entry of the log of the compass calibration for the RPA.
 *
 * @param {object} rpaObject - The RPA object.
 * @returns {number} The last entry of the log of the compass calibration for the RPA, or '-' if not found.
 * @throws {Error} If RPA is null or undefined.
 */
export const getCompassCalibrationLogLogLastEntry = rpaObject => {
  if (isNil(rpaObject)) {
    throw new Error('RPA not defined');
  }
  const compassCalibrationLog = getCompassCalibrationLog(rpaObject);
  const logLength = compassCalibrationLog.length;
  return get(compassCalibrationLog, `${logLength - 1}`, '-');
};
